import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  MessageSquare, PhoneCall, ShoppingBag, CalendarClock, Image, AlertTriangle,
  Phone, Mail, Share2, Globe, MessageCircle, FlaskConical, PlusCircle, Eye, PauseCircle,
  Check, X, ArrowRight,
} from "lucide-react";
import DashLayout from "@/components/dashboard/DashLayout";
import { StatusBadge } from "@/components/brand";
import { Reveal } from "@/lib/motion";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

const STATS = [
  { label: "Conversations today", value: "18", icon: MessageSquare },
  { label: "Calls answered", value: "23", icon: PhoneCall },
  { label: "Orders received", value: "12", icon: ShoppingBag },
  { label: "Follow-ups due", value: "4", icon: CalendarClock },
  { label: "Posts ready for approval", value: "3", icon: Image },
  { label: "Problems needing attention", value: "1", icon: AlertTriangle, warn: true },
];

const AI_STATUS = [
  { icon: Phone, name: "Riya", role: "Calling", status: "Working", color: "bg-orange-500" },
  { icon: MessageCircle, name: "Kabir", role: "WhatsApp", status: "Working", color: "bg-emerald-500" },
  { icon: Mail, name: "Meera", role: "Email", status: "Needs attention", color: "bg-sky-500" },
  { icon: Share2, name: "Aarav", role: "Social Media", status: "Setup required", color: "bg-rose-500" },
  { icon: Globe, name: "Zoya", role: "Website connection", status: "Working", color: "bg-violet-500" },
];

const ACTIVITY = [
  { icon: PhoneCall, text: "Customer call answered", detail: "Ramesh Kumar asked about AC prices — 2 min call", time: "9:42 AM" },
  { icon: ShoppingBag, text: "Order created", detail: "Order #1047 — Samsung 1.5-ton AC, ₹38,500", time: "9:47 AM" },
  { icon: MessageCircle, text: "WhatsApp reply sent", detail: "Delivery details shared with Priya Sharma", time: "10:15 AM" },
  { icon: ShoppingBag, text: "Tracking information shared", detail: "Order #1042 tracking link sent to Amit", time: "11:03 AM" },
  { icon: Image, text: "Social post prepared", detail: "Festive offer post ready for your approval", time: "11:30 AM" },
];

const APPROVALS = [
  { tag: "Important customer reply", title: "Bulk order enquiry — 20 units", desc: "A hotel chain asked for a quote on 20 air conditioners. AI drafted a reply with a special bulk price.", tone: "orange" },
  { tag: "Refund request", title: "Refund of ₹2,450 — Order #1042", desc: "Priya reports the mixer grinder arrived damaged. AI prepared a polite refund confirmation.", tone: "red" },
  { tag: "Special discount", title: "15% discount request", desc: "Regular customer Suresh asked for a discount on a refrigerator. AI suggests a 10% counter-offer.", tone: "orange" },
  { tag: "Social media post", title: "Diwali offer post + image", desc: "A festive post announcing 10% off all kitchen appliances this week, with a designed image.", tone: "orange" },
];

export default function Dashboard() {
  const [confirm, setConfirm] = useState(null); // { index, action }
  const [decided, setDecided] = useState({});
  const [businessName, setBusinessName] = useState("Your Business");
  const navigate = useNavigate();

  useEffect(() => {
    fetch("/wp-json/csai/v1/business", { credentials: "same-origin" })
      .then(async (response) => {
        if (response.status === 401) {
          window.location.href = "/login";
          return null;
        }
        const data = await response.json();
        if (data?.business?.business_name) setBusinessName(data.business.business_name);
        return data;
      })
      .catch(() => toast.error("Could not load your business profile."));
  }, []);

  const act = (i, action) => {
    setDecided({ ...decided, [i]: action });
    setConfirm(null);
    toast.success(`Design preview: request ${action}`);
  };

  return (
    <DashLayout title="Home" subtitle="" businessName={businessName}>
      <div data-testid="dashboard-page">
        <Reveal>
          <h1 className="font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
            Namaste, {businessName}
          </h1>
          <p className="mt-1 text-sm text-slate-500">Here is what your AI employee did today.</p>
        </Reveal>

        {/* Stat cards */}
        <div className="mt-7 grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-6" data-testid="dashboard-stat-cards">
          {STATS.map((s, i) => (
            <Reveal key={s.label} delay={i * 0.05}>
              <div
                className={`h-full rounded-2xl border bg-white p-4 ${s.warn ? "border-red-200" : "border-slate-100"}`}
                data-testid={`stat-card-${i}`}
              >
                <s.icon className={`h-5 w-5 ${s.warn ? "text-red-500" : "text-orange-600"}`} />
                <p className="mt-3 font-display text-3xl font-bold text-slate-900">{s.value}</p>
                <p className="mt-0.5 text-xs font-medium text-slate-500">{s.label}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <div className="mt-6 grid gap-6 xl:grid-cols-2">
          {/* AI Employee Status */}
          <Reveal>
            <section className="h-full rounded-2xl border border-slate-100 bg-white p-6" data-testid="ai-employee-status-section">
              <div className="flex items-center justify-between">
                <h2 className="font-display text-lg font-bold text-slate-900">AI Employee Status</h2>
                <button
                  data-testid="ai-status-fix-all"
                  onClick={() => toast.info("Design preview: connection settings open here")}
                  className="text-sm font-semibold text-orange-700 hover:underline"
                >
                  Manage
                </button>
              </div>
              <p className="mt-1 text-xs text-slate-400">Your AI team — every agent with a name, always on duty</p>
              <ul className="mt-3 divide-y divide-slate-100">
                {AI_STATUS.map((s) => (
                  <li key={s.name} className="flex items-center justify-between py-3.5" data-testid={`ai-status-${s.role.toLowerCase().replace(/\s/g, "-")}`}>
                    <span className="flex items-center gap-3">
                      <span className={`relative flex h-10 w-10 items-center justify-center rounded-full font-display text-sm font-bold text-white ${s.color}`}>
                        {s.name[0]}
                        <s.icon className="absolute -bottom-1 -right-1 h-4 w-4 rounded-full bg-white p-0.5 text-slate-500 ring-1 ring-slate-200" />
                      </span>
                      <span>
                        <span className="block text-sm font-bold text-slate-900">{s.name}</span>
                        <span className="block text-xs text-slate-400">{s.role} agent</span>
                      </span>
                    </span>
                    <StatusBadge label={s.status} />
                  </li>
                ))}
              </ul>
            </section>
          </Reveal>

          {/* Today's Activity */}
          <Reveal delay={0.08}>
            <section className="h-full rounded-2xl border border-slate-100 bg-white p-6" data-testid="todays-activity-section">
              <h2 className="font-display text-lg font-bold text-slate-900">Today's Activity</h2>
              <ul className="mt-4 space-y-1">
                {ACTIVITY.map((a, i) => (
                  <li key={i} className="flex gap-4 rounded-xl px-2 py-3 transition-colors hover:bg-slate-50" data-testid={`activity-item-${i}`}>
                    <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-orange-50 text-orange-600">
                      <a.icon className="h-4 w-4" />
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-slate-800">{a.text}</p>
                      <p className="truncate text-xs text-slate-500">{a.detail}</p>
                    </div>
                    <span className="shrink-0 text-xs font-medium text-slate-400">{a.time}</span>
                  </li>
                ))}
              </ul>
            </section>
          </Reveal>
        </div>

        {/* Needs Your Approval */}
        <Reveal>
          <section className="mt-6 rounded-2xl border-2 border-orange-200 bg-orange-50/40 p-6" data-testid="needs-approval-section">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="font-display text-lg font-bold text-slate-900">Needs Your Approval</h2>
                <p className="text-sm text-slate-500">Nothing happens with these until you decide.</p>
              </div>
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-orange-600 font-display text-sm font-bold text-white">
                {APPROVALS.length - Object.keys(decided).length}
              </span>
            </div>
            {APPROVALS.length - Object.keys(decided).length === 0 && (
              <div className="mt-6 rounded-xl border border-dashed border-emerald-300 bg-emerald-50/50 p-8 text-center" data-testid="approval-empty-state">
                <Check className="mx-auto h-8 w-8 text-emerald-500" />
                <p className="mt-2 font-display text-base font-bold text-slate-900">All caught up!</p>
                <p className="text-sm text-slate-500">New approval requests will appear here.</p>
              </div>
            )}
            <div className="mt-5 grid gap-4 md:grid-cols-2">
              {APPROVALS.map((a, i) =>
                decided[i] ? null : (
                  <div key={i} className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm" data-testid={`approval-card-${i}`}>
                    <span className={`inline-block rounded-full px-2.5 py-1 text-xs font-bold ${a.tone === "red" ? "bg-red-50 text-red-700" : "bg-orange-100 text-orange-800"}`}>
                      {a.tag}
                    </span>
                    <h3 className="mt-3 font-display text-base font-bold text-slate-900">{a.title}</h3>
                    <p className="mt-1 text-sm leading-relaxed text-slate-600">{a.desc}</p>
                    <div className="mt-4 flex gap-2.5">
                      <button
                        data-testid={`approval-approve-${i}`}
                        onClick={() => setConfirm({ index: i, action: "approved" })}
                        className="flex flex-1 items-center justify-center gap-1.5 rounded-xl bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-emerald-700"
                      >
                        <Check className="h-4 w-4" /> Approve
                      </button>
                      <button
                        data-testid={`approval-reject-${i}`}
                        onClick={() => setConfirm({ index: i, action: "rejected" })}
                        className="flex flex-1 items-center justify-center gap-1.5 rounded-xl border-2 border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50"
                      >
                        <X className="h-4 w-4" /> Reject
                      </button>
                      <button
                        data-testid={`approval-view-${i}`}
                        onClick={() => navigate("/inbox")}
                        className="rounded-xl border-2 border-slate-200 bg-white px-3.5 py-2.5 text-slate-500 transition-colors hover:bg-slate-50"
                        aria-label="View conversation"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                )
              )}
            </div>
          </section>
        </Reveal>

        {/* Quick actions */}
        <Reveal>
          <section className="mt-6" data-testid="quick-actions-section">
            <h2 className="font-display text-lg font-bold text-slate-900">Quick actions</h2>
            <div className="mt-4 flex flex-wrap gap-3">
              {[
                { icon: FlaskConical, label: "Test AI", fn: () => toast.info("Design preview: test your AI employee here") },
                { icon: PlusCircle, label: "Add business information", fn: () => navigate("/inbox") },
                { icon: Image, label: "Create a social post", fn: () => toast.info("Design preview: post creator opens here") },
                { icon: MessageSquare, label: "View conversations", fn: () => navigate("/inbox") },
                { icon: PauseCircle, label: "Pause AI", fn: () => toast.warning("Design preview: AI paused — customers get a polite 'we will call you back' message") },
              ].map((q, i) => (
                <button
                  key={q.label}
                  data-testid={`quick-action-${i}`}
                  onClick={q.fn}
                  className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-5 py-3 text-sm font-semibold text-slate-700 shadow-sm transition-all hover:border-orange-300 hover:text-orange-800 hover:shadow"
                >
                  <q.icon className="h-4 w-4" /> {q.label}
                </button>
              ))}
            </div>
          </section>
        </Reveal>
      </div>

      <AlertDialog open={!!confirm} onOpenChange={() => setConfirm(null)}>
        <AlertDialogContent data-testid="approval-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display">
              {confirm?.action === "approved" ? "Approve this action?" : "Reject this request?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirm?.action === "approved"
                ? "The AI will send the prepared reply to the customer right away."
                : "The AI will politely inform the customer, and you can handle it yourself later."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="approval-confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="approval-confirm-ok"
              className={confirm?.action === "approved" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-red-600 hover:bg-red-700"}
              onClick={() => act(confirm.index, confirm.action)}
            >
              {confirm?.action === "approved" ? "Yes, approve" : "Yes, reject"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashLayout>
  );
}
